import * as React from 'react';
import ReactHtmlParser from 'react-html-parser';
import { CurrencyFormatter } from '@fnv-core/formatting/currency';
import { DateFormatter } from '@fnv-core/formatting/date';
import { DecimalFormatter } from '@fnv-core/formatting/decimal';
import { Tokenizer } from '@fnv-core/tokens';
import { FormControlProps } from '../../interfaces/FormControlProps';
import { IFormFieldDefinition, IOption } from '../../interfaces/Fields';
import { FormFieldStateHelpers } from './FormFieldStateHelpers';
import { AjaxCallHelpers } from './AjaxCallHelpers';
import { InputState } from '../Input';
import { MaskedInputState } from '../MaskedInput';
import { FormControlSpecifics } from './form-control-specifics';
import { FormikHelpers } from './FormikHelpers';

export class FormControlHelpers {
    static isEmpty(value) {
        return value === undefined || value === null || value === '';
    }

    /**
     * Formats the given value to a friendly formatted representation to display in for example summaries, readonly mode etcetera.
     * @param value The value to format.
     * @param formFieldDefinition The form field definition to use.
     * @returns A friendly formatted representation of the value.
     */
    static convertValueToType(value: any, formFieldDefinition: IFormFieldDefinition) {
      if (!formFieldDefinition.dataType) {
        return value;
      }

      let displayValue = value;

      // Dates
      if (formFieldDefinition.dataType === "date") {
        displayValue = new DateFormatter().format(value);
      }
      // Numbers
      else if (formFieldDefinition.dataType === "decimal") {
        displayValue = new DecimalFormatter().format(value);
      }
      // Currencies
      else if (formFieldDefinition.dataType === "currency") {
        let currencyFormatted = new CurrencyFormatter().format(value, {
          // Omit the currency symbol, because a custom template can be provided below
          currency: null,
          decimalSeparator: formFieldDefinition.currencyFormat ? formFieldDefinition.decimalSeparator : null,
          stripDecimals: formFieldDefinition.hasDecimals !== undefined && !formFieldDefinition.hasDecimals,
        });

        if (formFieldDefinition.currencyFormat) {
          currencyFormatted = Tokenizer.replaceTokens(formFieldDefinition.currencyFormat, [{ name: 'value', value: currencyFormatted }]);
        }

        displayValue = currencyFormatted;
      }
      // Arrays
      else if (formFieldDefinition.dataType === 'array' && Array.isArray(value)) {
        // Assume as strongly typed
        const arrayValue = value as (string | IOption)[];
        const joiner = formFieldDefinition.displayMode === 'list'
          ? '\n'
          : ', ';
        displayValue = arrayValue
          .map(v => typeof (v) === 'string' ? v : v.text || v.label)
          .join(joiner);
      }

      return displayValue;
    }

    static baseProperties(component: React.Component<FormControlProps>) {
        const props = component.props;
        let payloadFieldValue = "";
        if (props.formConfig && !!(props.formConfig as any).usePayloadAsDefaultForInput) {
            payloadFieldValue = (props.formController && props.formController.getPayload && props.formController.getPayload() || {})[props.formFieldDefinition.name]; 
        }
        let value = FormControlHelpers.convertValueToType(props.field.value || 
            payloadFieldValue || "", 
            component.props.formFieldDefinition); 
        return {
            ref: props.formFieldDefinition.name,
            id: props.formFieldDefinition.name,
            value: value,
            placeholder: props.formFieldDefinition.placeholder,
            name: props.formFieldDefinition.name
        };
    }

    static handleTriggerFieldDependingOnCondition(component: React.Component<FormControlProps>, value) {
        const props = component.props;
        if (props.formFieldDefinition.triggerFieldWithCondition !== undefined) {
            if (value === props.formFieldDefinition.triggerFieldWithCondition.value) {

                AjaxCallHelpers.triggerDatasource(component.props.formikBag,
                    component.props.formConfig,
                    props.formFieldDefinition.triggerFieldWithCondition.datasource 
                );

            }
        }
    }
    
    static onChangeSetValue(component: React.Component<FormControlProps>) {
        const props = component.props;
        return {
            onChange: function(value: any) {
                let newValue = value;
                if (value && value.value) {
                    if (value.text) {
                        newValue.label = value.text;
                    }
                }

                if (value && value.data) {                    
                    component.props.formController.setPayload(value.data);
                }
                
                if (component.props.formFieldDefinition.isDependentField) {                    
                    component.props.formController.setState({[props.formFieldDefinition.name]: newValue});
                }
                
                FormFieldStateHelpers.clearMarkedFields(component);
                props.formikBag.setFieldValue(props.formFieldDefinition.name, newValue);
                
                if ((newValue && newValue.value && newValue.value != "") || 
                    (newValue && newValue.value === undefined && newValue != "")) {
                    props.formikBag.setFieldError(props.formFieldDefinition.name, "");        
                }

                FormControlHelpers.handleTriggerFieldDependingOnCondition(component, newValue);
                if (!!props.formFieldDefinition.mapPayloadFieldsOnChange) {
                    FormikHelpers.mapValuesToFields(component, value.data);  
                }    
                if (props.formFieldDefinition.triggerDatasource) {
                    // Known issue: formik setFieldValue is async. By using a time out, we work around this.
                    setTimeout(() => {
                        if (FormControlHelpers.isValid(component)) {
                            // Only get data when input is valid
                            AjaxCallHelpers.getRelatedData(component as any, value);
                        }
                    }, 1);
                }
            }
        };
    }

    static onChangeSetValueViaEvent(component: React.Component<FormControlProps>) {
        const props = component.props;
        return {
            onChange: function(event: any) {
                FormFieldStateHelpers.clearMarkedFields(component);

                let value = event.target.value;
                
                // workaround voor kentico/api value mismatch
                // value kan ook iets anders bevatten dan true/false (bijvoorbeeld Ja/Nee, Man/Vrouw etc)
                if (value === "true") {
                    value = true;
                }
                if (value === "false") {
                    value = false;
                }

                props.formikBag.setFieldTouched(props.formFieldDefinition.name, true, false);
                props.formikBag.setFieldValue(props.formFieldDefinition.name, value, true).then(() => {
                    const error = component.props.formController.validateField(component.props.formFieldDefinition, component.props.formikBag);
                    component.props.formikBag.setFieldError(component.props.formFieldDefinition.name, error);

                    FormControlHelpers.handleTriggerFieldDependingOnCondition(component, event.target.value);

                    // Known issue: formik setFieldValue is async. By using a time out, we work around this.
                    if (FormControlHelpers.isValid(component)) {
                        // Only get data when input is valid
                        AjaxCallHelpers.getRelatedData(component as any, value);
                    }
                });
                
            }
        };
    }
    static onRadioButtonChangeSetValueViaEvent(component: React.Component<FormControlProps>, label: string) {
        const props = component.props;
        return {
            onChange: function(event: any) {
                FormFieldStateHelpers.clearMarkedFields(component);

                let value = event.target.value;
                
                // workaround voor kentico/api value mismatch
                // value kan ook iets anders bevatten dan true/false (bijvoorbeeld Ja/Nee, Man/Vrouw etc)
                if (value === "true") {
                    value = true;
                }
                if (value === "false") {
                    value = false;
                }
                if (component.props.formFieldDefinition.datasource) {
                    props.formikBag.setFieldValue(props.formFieldDefinition.name, {
                        label: label,
                        value: value
                    });
                } else {
                    props.formikBag.setFieldValue(props.formFieldDefinition.name, value);
                }
                FormControlHelpers.handleTriggerFieldDependingOnCondition(component, event.target.value);

                // Known issue: formik setFieldValue is async. By using a time out, we work around this.
                setTimeout(() => {
                    if (FormControlHelpers.isValid(component)) {
                        // Only get data when input is valid
                        AjaxCallHelpers.getRelatedData(component as any, value);
                    }
                }, 1);
            }
        };
    }
    static onChangeSetValueDoAction(
        component: React.Component<FormControlProps>,
        handleDatasource = (value: string) => {}
    ) {
        return {
            onChange: function(e) {
                FormFieldStateHelpers.clearMarkedFields(component);
                FormFieldStateHelpers.updateFormikField(component as any, e.target.value);
                handleDatasource(e.target.value);
                FormControlHelpers.handleTriggerFieldDependingOnCondition(component, e.target.value);
            }
        };
    }

    static onInputFocusChangeBlur(component: React.Component<FormControlProps>) {
        return {
            onChange(event) {
                FormFieldStateHelpers.clearMarkedFields(component);

                let value = event.target.value;

                // looking at the way this used to work, Kentico expects true as string instead of boolean.
                // To uncheck the checkbox, we check if the event checks or unchecks and then set value accordingly.
                if (component.props.formFieldDefinition.type === "checkbox") {
                    value = event.target.checked ? event.target.value : 'false';
                }

                if (component.props.formFieldDefinition.type == "fileupload") {
                    
                    let result = false;
                    if ((component as any).onInputChange) {
                        result = (component as any).onInputChange(event);
                    }

                    if (result) {
                        value = event.currentTarget.files[0];
                    } else {
                        return;
                    }
                }
                
                // Can we update our field?
                if (!FormFieldStateHelpers.canUpdateField(component, value)) {
                    return;
                }

                // Update formik field
                component.props.formikBag.setFieldTouched(component.props.formFieldDefinition.name, true, false);
                // By using .then, we have no need for a setTimeout before validating the value.
                component.props.formikBag.setFieldValue(component.props.formFieldDefinition.name, value, true).then((res) => {
                    const error = component.props.formController.validateField(component.props.formFieldDefinition, component.props.formikBag);
                    component.props.formikBag.setFieldError(component.props.formFieldDefinition.name, error);
                    
                    if (FormControlHelpers.isValid(component)) {
                        // Only get data when input is valid

                        if (component.props.formController.props.onFieldChange) {
                            component.props.formController.props.onFieldChange(
                                component.props.formController.props.formName, 
                                component.props.formFieldDefinition.name,
                                value);
                        }

                        FormControlSpecifics.proces(component, value);

                        AjaxCallHelpers.getRelatedData(component as any, value);
                    }
                });
            },
            onBlur(e) {
                component.props.field.onBlur(e);
            },
            
        };
    }
    static isValid(component: React.Component<FormControlProps>) {
        return !this.isInValid(component);
    }

    // Double of isTouched has errors
    static isInValid(component: React.Component<FormControlProps>, fieldName: string = null) {
        const props = component.props;
        fieldName = fieldName || props.formFieldDefinition.name;
        return props.form.touched[fieldName] && props.form.errors[fieldName];
    }
    static inputProperties(component: React.Component<FormControlProps>) {
        const { readOnly } = FormFieldStateHelpers.getFieldState(component);
        return {
            ...this.baseProperties(component),
            ...this.onInputFocusChangeBlur(component),
            readOnly
        };
    }
    static invalidFeedbackComponent(component: React.Component<FormControlProps>) {
        const props = component.props;
        return this.isInValid(component) ? (
            <div className="form__invalid-feedback">
                {ReactHtmlParser(props.form.errors[props.formFieldDefinition.name])}
            </div>
        ) : null;
    }
    static getTimeDelay(component: React.Component<FormControlProps>, timeDelay: number = 1000) {
        // Set timer
        const timerComponent = component as any;

        if (timerComponent.state.timer) {
            clearTimeout(timerComponent.state.timer);
        } 

        if (component.props.formConfig.datasourceTimer !== undefined) {
            timeDelay = component.props.formConfig.datasourceTimer;
        }

        return timeDelay;
    }
    static getCounter(component: React.Component<FormControlProps, InputState | MaskedInputState>) {
        let counter = component.state.dataSourceCounter;
        counter++;
        
        if ((component as any).unmounted !== undefined && !!(component as any).unmounted) {
            return;
        }

        component.setState({ dataSourceCounter: counter });

        return counter;
    }

    static replaceBracketFieldsWithValues = (component: React.Component<FormControlProps>, targetString : string) => {
        var rxp = /{([^}]+)}/g,
        curMatch;
        let result = targetString;
        while( curMatch = rxp.exec( targetString ) ) {
            let value = "";
            if (component.props.formikBag.values[curMatch[1]]) {
              value = component.props.formikBag.values[curMatch[1]];
            }
            result = result.replace("{" + curMatch[1] + "}", value);
        }
    
        return result;
      }

    static replaceTaggedEmailAddressesWithoutTags = (value : string) => {        
        let result = (value || "").toString();
        let regularExpression = /<.+?@.+?>/g;
        const matches = result.match(regularExpression);
        if (matches) {
            matches.map((match) => {
              const email = match.slice(1, -1);              
              result = result.replace("<" + email + ">", email);                
            });
        }
        return result;  
    }   
}
